// @flow
// Types
// ========================================================
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/browser';
import type { Action } from 'redux/ducks/helpers';

export type User = {
  id: number,
  email: string,
  firstName: string,
  lastName: string,
  name: string,
  url: string,
};

export type SessionStateType = {
  currentUser: ?User,
  accessToken: ?string,
  tokenType: ?string,
  expiry: ?string,
  client: ?string,
  uid: ?string,
  authenticated: boolean,
  error: any,
};

// Actions
// ========================================================
const constForName = name => `go4ellisMobile/session/${name}`;

export const CREATE_SESSION = constForName('CREATE_SESSION');
export const CREATE_SESSION_SUCCESS = constForName('CREATE_SESSION_SUCCESS');
export const CREATE_SESSION_ERROR = constForName('CREATE_SESSION_ERROR');
export const DESTROY_SESSION = constForName('DESTROY_SESSION');
export const DESTROY_SESSION_SUCCESS = constForName('DESTROY_SESSION_SUCCESS');
export const DESTROY_SESSION_ERROR = constForName('DESTROY_SESSION_ERROR');
export const RELOAD_SESSION = constForName('RELOAD_SESSION');
export const RELOAD_SESSION_SUCCESS = constForName('RELOAD_SESSION_SUCCESS');
export const RELOAD_SESSION_ERROR = constForName('RELOAD_SESSION_ERROR');
export const RELOAD_SESSION_FAILURE = constForName('RELOAD_SESSION_FAILURE');
export const RELOAD_USER_SUCCESS = constForName('RELOAD_USER_SUCCESS');
export const RELOAD_USER_ERROR = constForName('RELOAD_USER_ERROR');
export const UPDATE_SESSION = constForName('UPDATE_SESSION');
export const INVALID_SESSION = constForName('INVALID_SESSION');
export const GOOGLE_LOGIN = constForName('GOOGLE_LOGIN');
export const GOOGLE_LOGIN_SUCCESS = constForName('GOOGLE_LOGIN_SUCCESS');
export const GOOGLE_LOGIN_ERROR = constForName('GOOGLE_LOGIN_ERROR');
export const MICROSOFT_LOGIN = constForName('MICROSOFT_LOGIN');
export const MICROSOFT_LOGIN_SUCCESS = constForName('MICROSOFT_LOGIN_SUCCESS');
export const MICROSOFT_LOGIN_ERROR = constForName('MICROSOFT_LOGIN_ERROR');
export const ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH = constForName('ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH');
export const ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS = constForName('ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS');
export const ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR = constForName('ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR');

// Reducer Functions
// ========================================================
function updateSessionReducer(state: SessionStateType, action: Action): SessionStateType {
  identifyLogRocketUser(action.payload.session.currentUser);
  identifySentryUser(action.payload.session.currentUser);

  return {
    ...state,
    ...action.payload.session,
    error: null,
    sessionExpired: false,
  };
}

function destroySessionReducer(state: SessionStateType, payload) {
  if (payload?.inactive) return { ...defaultState(), sessionExpired: true, error: payload.message };

  return defaultState();
}

function errorReducer(state: SessionStateType, action) {
  return {
    ...state,
    error: action.payload,
  };
}

function reloadSessionReducer(state: SessionStateType, action) {
  return {
    ...state,
    ...action.payload,
    error: null,
    sessionExpired: false,
  };
}

function reloadUserReducer(state: SessionStateType, action) {
  return {
    ...state,
    currentUser: action.payload,
  };
}

function clearErrorsReducer(state: SessionStateType, action) {
  return {
    ...state,
    error: null,
    sessionExpired: false,
  };
}

// Action Creators
// ========================================================
export function createSession(credentials: any) {
  return {
    type: CREATE_SESSION,
    payload: credentials,
  };
}

export function destroySession(payload) {
  return {
    type: DESTROY_SESSION,
    payload,
  };
}

export const reloadSession = () => {
  return {
    type: RELOAD_SESSION,
  };
};

export function googleLogin(credentials) {
  return {
    type: GOOGLE_LOGIN,
    payload: credentials,
  };
}

export function microsoftLogin(credentials) {
  return {
    type: MICROSOFT_LOGIN,
    payload: credentials,
  };
}

export function athleticTrainerSignUpWithOAuth(credentials) {
  return {
    type: ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH,
    payload: credentials,
  };
}

function identifyLogRocketUser({ id, name, email }) {
  LogRocket.identify(id, { name, email });
}

function identifySentryUser({ id, name, email }) {
  LogRocket.identify(id, { name, email });
  Sentry.configureScope(scope => {
    scope.setUser({
      id,
      email,
    });
  });
}

// Reducer
// ========================================================
export const defaultState = () => ({
  currentUser: {},
  accessToken: null,
  tokenType: null,
  expiry: null,
  client: null,
  uid: null,
  authenticated: false,
  error: null,
  sessionExpired: false,
});

export default function reducer(state: SessionStateType = defaultState(), action: Action): SessionStateType {
  if (process.env.REACT_APP_LOG_ACTIONS) {
    console.info(action);
  }
  switch (action.type) {
    case CREATE_SESSION_SUCCESS:
    case UPDATE_SESSION:
    case GOOGLE_LOGIN_SUCCESS:
    case MICROSOFT_LOGIN_SUCCESS:
      return updateSessionReducer(state, action);
    case DESTROY_SESSION_SUCCESS:
      return destroySessionReducer(state, action.payload);
    case RELOAD_SESSION_SUCCESS:
      return reloadSessionReducer(state, action);
    case CREATE_SESSION_ERROR:
    case RELOAD_SESSION_ERROR:
    case INVALID_SESSION:
    case GOOGLE_LOGIN_ERROR:
    case MICROSOFT_LOGIN_ERROR:
      return errorReducer(state, action);
    case RELOAD_USER_SUCCESS:
      return reloadUserReducer(state, action);
    case CREATE_SESSION:
    case GOOGLE_LOGIN:
    case MICROSOFT_LOGIN:
      return clearErrorsReducer(state, action);
    default:
      return state;
  }
}
